import { useEffect, useState } from 'react'
import heroImage from '../assets/coming-soon.png'
import BrandMark from '../components/ui/BrandMark'
import './ComingSoon.css'

/** The launch moment is fixed in Islamabad time so every visitor sees the same countdown. */
const LAUNCH_DATE = new Date('2026-10-05T00:00:00+05:00')

type TimeLeft = {
  days: number
  hours: number
  minutes: number
  seconds: number
}

function getTimeLeft(target: Date): TimeLeft {
  const diff = Math.max(0, target.getTime() - Date.now())
  const totalSeconds = Math.floor(diff / 1000)

  return {
    days: Math.floor(totalSeconds / 86400),
    hours: Math.floor((totalSeconds % 86400) / 3600),
    minutes: Math.floor((totalSeconds % 3600) / 60),
    seconds: totalSeconds % 60,
  }
}

const pad = (value: number) => String(value).padStart(2, '0')

function Countdown({ target }: { target: Date }) {
  const [timeLeft, setTimeLeft] = useState(() => getTimeLeft(target))

  useEffect(() => {
    const id = setInterval(() => setTimeLeft(getTimeLeft(target)), 1000)
    return () => clearInterval(id)
  }, [target])

  const units = [
    { label: 'Days', value: timeLeft.days },
    { label: 'Hours', value: timeLeft.hours },
    { label: 'Minutes', value: timeLeft.minutes },
    { label: 'Seconds', value: timeLeft.seconds },
  ]

  return (
    <div className="countdown" role="timer">
      <span className="sr-only">
        {timeLeft.days} days, {timeLeft.hours} hours, {timeLeft.minutes} minutes
        and {timeLeft.seconds} seconds until launch
      </span>

      {units.map((unit) => (
        <div className="countdown__unit" key={unit.label} aria-hidden="true">
          <span className="countdown__value">{pad(unit.value)}</span>
          <span className="countdown__label">{unit.label}</span>
        </div>
      ))}
    </div>
  )
}

const SOCIALS = [
  {
    name: 'Instagram',
    href: 'https://instagram.com/sultanmotors28',
    path: 'M12 2.2c3.2 0 3.6 0 4.9.07 1.17.05 1.8.25 2.23.41.56.22.96.48 1.38.9.42.42.68.82.9 1.38.16.42.36 1.06.41 2.23.06 1.27.07 1.65.07 4.86s0 3.6-.07 4.86c-.05 1.17-.25 1.8-.41 2.23-.22.56-.48.96-.9 1.38-.42.42-.82.68-1.38.9-.42.16-1.06.36-2.23.41-1.27.06-1.65.07-4.86.07s-3.6 0-4.86-.07c-1.17-.05-1.8-.25-2.23-.41a3.8 3.8 0 0 1-1.38-.9 3.8 3.8 0 0 1-.9-1.38c-.16-.42-.36-1.06-.41-2.23C2.2 15.6 2.2 15.2 2.2 12s0-3.6.07-4.86c.05-1.17.25-1.8.41-2.23.22-.56.48-.96.9-1.38.42-.42.82-.68 1.38-.9.42-.16 1.06-.36 2.23-.41C8.46 2.2 8.84 2.2 12 2.2Zm0 1.8c-3.15 0-3.5.01-4.74.07-1.14.05-1.76.24-2.17.4-.55.21-.94.47-1.35.88-.41.41-.67.8-.88 1.35-.16.41-.35 1.03-.4 2.17-.06 1.24-.07 1.6-.07 4.74s.01 3.5.07 4.74c.05 1.14.24 1.76.4 2.17.21.55.47.94.88 1.35.41.41.8.67 1.35.88.41.16 1.03.35 2.17.4 1.24.06 1.6.07 4.74.07s3.5-.01 4.74-.07c1.14-.05 1.76-.24 2.17-.4.55-.21.94-.47 1.35-.88.41-.41.67-.8.88-1.35.16-.41.35-1.03.4-2.17.06-1.24.07-1.6.07-4.74s-.01-3.5-.07-4.74c-.05-1.14-.24-1.76-.4-2.17a3.6 3.6 0 0 0-.88-1.35 3.6 3.6 0 0 0-1.35-.88c-.41-.16-1.03-.35-2.17-.4C15.5 4.01 15.15 4 12 4Zm0 3.06a4.94 4.94 0 1 1 0 9.88 4.94 4.94 0 0 1 0-9.88Zm0 1.8a3.14 3.14 0 1 0 0 6.28 3.14 3.14 0 0 0 0-6.28Zm5.14-3.2a1.15 1.15 0 1 1 0 2.3 1.15 1.15 0 0 1 0-2.3Z',
  },
  {
    name: 'Facebook',
    href: 'https://facebook.com/sultanmotors28',
    path: 'M22 12.06C22 6.5 17.52 2 12 2S2 6.5 2 12.06c0 5.02 3.66 9.18 8.44 9.94v-7.03H7.9v-2.9h2.54V9.85c0-2.52 1.5-3.91 3.77-3.91 1.09 0 2.24.2 2.24.2v2.46h-1.26c-1.24 0-1.63.78-1.63 1.57v1.89h2.78l-.45 2.9h-2.33V22c4.78-.76 8.44-4.92 8.44-9.94Z',
  },
]

/* The photograph fills the viewport. The message and live countdown sit over
   its darkened centre while the showroom stays visible around them. */
function Hero() {
  return (
    <section className="hero">
      <div className="hero__panel">
        <img
          className="hero__img"
          src={heroImage}
          alt="The Sultan Motors 28 showroom floor"
        />
      </div>

      <header className="hero__bar">
        <a className="brand" href="/" aria-label="Sultan Motors 28 home">
          <span className="brand__mark" aria-hidden="true">
            <BrandMark className="brand__logo" />
          </span>
          <span className="brand__text">
            <strong>Sultan Motors</strong>
            <small>THE CAR LOUNGE</small>
          </span>
        </a>

      </header>

      <div className="hero__content">
        <div className="hero__copy">
          <p className="hero__eyebrow">
            <span className="hero__rule" aria-hidden="true" />
            Something exceptional is on the way
          </p>

          <h1 className="hero__title">
            Coming <em>Soon</em>
          </h1>

          <a className="hero__domain" href="https://sultanmotors28.com">
            sultanmotors28.com
          </a>

          <Countdown target={LAUNCH_DATE} />
        </div>
      </div>

      <footer className="hero__foot">
        <ul className="hero__socials">
          {SOCIALS.map((social) => (
            <li key={social.name}>
              <a
                className="hero__social"
                href={social.href}
                target="_blank"
                rel="noreferrer"
                aria-label={social.name}
              >
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <path d={social.path} />
                </svg>
                <span>{social.name}</span>
              </a>
            </li>
          ))}
        </ul>

        <p className="hero__note">Islamabad, Pakistan</p>
      </footer>
    </section>
  )
}

export default Hero
